/**
 * A line's text box with the @ picker (slice 6d spec §5.5): where the cursor
 * is, the `@…` being typed before it, and what a pick and each key do.
 */
import { useLayoutEffect, useRef, useState, type KeyboardEvent } from "react";
import type { LineEditor } from "./use-line-editor";
import { boxKey, insertMention, pickerState, type Mention } from "./mentions";

export function useMentionBox(ed: LineEditor) {
  const box = useRef<HTMLTextAreaElement>(null);
  /** What Enter picks while the picker is open (the picker sets it). */
  const first = useRef<(() => void) | null | undefined>(null);
  const draft = ed.editing?.draft ?? "";
  const [cursor, setCursor] = useState(draft.length);
  /** Where an `@` was picked or closed with Escape: the picker stays shut for it. */
  const [closed, setClosed] = useState<number | null>(null);
  /** Where the caret goes after a pick, once the new text has rendered. */
  const [caret, setCaret] = useState<number | null>(null);
  useLayoutEffect(() => {
    if (caret === null) return;
    box.current?.setSelectionRange(caret, caret);
    setCaret(null);
  }, [caret]);
  const { start, query } = pickerState(draft, cursor, closed);
  const pick = (picked: Mention) => {
    const m = ed.named(picked);
    const next = insertMention(draft, start, cursor, m.title);
    ed.mention(m, next.draft);
    setCursor(next.cursor);
    setClosed(start);
    setCaret(next.cursor);
  };
  const actions = {
    pick: () => first.current?.(),
    save: ed.save,
    close: () => setClosed(start),
    cancel: ed.cancel,
  };
  return {
    box,
    first,
    draft,
    query,
    pick,
    change: (value: string, at: number) => {
      ed.change(value.replace(/\n/g, " "));
      setCursor(at);
    },
    moved: (at: number) => setCursor(at),
    key: (e: KeyboardEvent) => {
      const act = boxKey(e.key, query !== null);
      if (act === "pick" || act === "save") e.preventDefault();
      if (act) actions[act]();
    },
  };
}
